import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { LifeBuoy, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { capturePostHogEvent } from "@/lib/posthog";

const CATEGORIES = [
  { value: "billing", label: "Billing or payments" },
  { value: "technical", label: "Technical issue" },
  { value: "account", label: "Account or login" },
  { value: "feedback", label: "Product feedback" },
  { value: "other", label: "Something else" },
];

export function ParentSupportDialog() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<string>("other");

  const submit = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/parent/support", { subject: subject.trim(), message: message.trim(), category });
    },
    onSuccess: () => {
      capturePostHogEvent("parent_support_ticket_created", { category });
      toast({ title: "We got it", description: "Our team will respond to your account email shortly." });
      setOpen(false);
      setSubject("");
      setMessage("");
      setCategory("other");
    },
    onError: (e: any) => toast({ title: "Could not send", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="support-trigger">
          <LifeBuoy className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Get help</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Contact EduFocal support</DialogTitle>
          <DialogDescription>
            Send us a message and we will follow up via email. For urgent payment issues, email support@edufocalacademy.com directly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="support-category">What's it about?</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="support-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-subject">Subject</Label>
            <Input id="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required maxLength={200} data-testid="support-subject" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-message">Message</Label>
            <Textarea id="support-message" value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={5000} rows={5} data-testid="support-message" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={submit.isPending || subject.trim().length < 3 || message.trim().length < 5} data-testid="support-submit">
              {submit.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
